import { useEffect, useState } from "react";
import { Building2, Pencil, Plus, Trash2, X } from "lucide-react";
import api from "../lib/api";
import { money } from "../lib/format";
import { notify } from "../lib/toast";

const COLORS = ["blue", "green", "amber", "violet"];

function AccountModal({ open, onClose, onSave, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: "",
    bank: "",
    balance: 0,
    last_four: "",
    color: "blue",
    currency: "TRY",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          name: initial.name || "",
          bank: initial.bank || "",
          balance: initial.balance ?? 0,
          last_four: initial.last_four || "",
          color: initial.color || "blue",
          currency: initial.currency || "TRY",
        });
      } else {
        setForm({ name: "", bank: "", balance: 0, last_four: "", color: "blue", currency: "TRY" });
      }
      setError("");
    }
  }, [open, initial]);

  if (!open) return null;

  const submit = async () => {
    if (!form.bank.trim()) return setError("Banka adı gerekli");
    setBusy(true);
    setError("");
    try {
      await onSave({ ...form, balance: Number(form.balance) || 0, name: form.name || form.bank });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="account-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">{isEdit ? "DÜZENLE" : "YENİ HESAP"}</span>
            <h2>{isEdit ? "Hesabı düzenle" : "Banka hesabı ekle"}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="close-account-modal">
            <X size={18} />
          </button>
        </div>
        <label>
          Banka adı
          <input
            value={form.bank}
            onChange={(e) => setForm({ ...form, bank: e.target.value })}
            placeholder="Örn. Garanti BBVA"
            data-testid="account-bank-input"
          />
        </label>
        <div className="form-grid">
          <label>
            Son 4 hane
            <input
              value={form.last_four}
              onChange={(e) => setForm({ ...form, last_four: e.target.value })}
              placeholder="1234"
              maxLength={4}
              data-testid="account-lastfour-input"
            />
          </label>
          <label>
            Bakiye (₺)
            <input
              type="number"
              value={form.balance}
              onChange={(e) => setForm({ ...form, balance: e.target.value })}
              data-testid="account-balance-input"
            />
          </label>
        </div>
        <label>
          Renk
          <div className="color-picker">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`swatch ${c} ${form.color === c ? "active" : ""}`}
                onClick={() => setForm({ ...form, color: c })}
                data-testid={`account-color-${c}`}
              />
            ))}
          </div>
        </label>
        {error ? <div className="form-error">{error}</div> : null}
        <button className="primary-btn full" onClick={submit} disabled={busy} data-testid="save-account-button">
          <Plus size={17} /> {busy ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Hesabı kaydet"}
        </button>
      </div>
    </div>
  );
}

export default function AccountsPage() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/accounts");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const total = items.reduce((s, a) => s + (a.balance || 0), 0);

  const onDelete = async (item) => {
    if (!window.confirm(`${item.bank} hesabı silinsin mi?`)) return;
    try {
      await api.delete(`/accounts/${item.id}`);
      notify.success("Hesap silindi");
      load();
    } catch {
      notify.error("Hesap silinemedi");
    }
  };

  const onSave = async (payload) => {
    if (editing) {
      await api.patch(`/accounts/${editing.id}`, payload);
      notify.success("Hesap güncellendi");
    } else {
      await api.post("/accounts", payload);
      notify.success("Hesap eklendi");
    }
    await load();
  };

  const closeModal = () => {
    setModal(false);
    setEditing(null);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">HESAPLAR</div>
          <h1>Banka Hesapları</h1>
          <p>Tüm banka ve kasa hesaplarınızın toplu görünümü.</p>
        </div>
        <div className="head-actions">
          <button className="primary-btn" onClick={() => setModal(true)} data-testid="add-account-button">
            <Plus size={17} /> Hesap ekle
          </button>
        </div>
      </div>

      <div className="account-summary panel" data-testid="account-summary">
        <div>
          <span>Toplam bakiye</span>
          <strong>{money(total)}</strong>
        </div>
        <div>
          <span>Hesap sayısı</span>
          <strong>{items.length}</strong>
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Yükleniyor…</div>
      ) : items.length === 0 ? (
        <div className="panel empty-state" data-testid="accounts-empty">
          <Building2 size={30} />
          <h3>Henüz hesap yok</h3>
          <p>Banka ya da kasa hesabınızı ekleyerek başlayın.</p>
          <button className="primary-btn" onClick={() => setModal(true)} data-testid="empty-add-account-btn">
            <Plus size={14} /> Hesap ekle
          </button>
        </div>
      ) : (
        <div className="account-grid">
          {items.map((a) => (
            <div className={`account-card ${a.color}`} key={a.id} data-testid={`account-${a.id}`}>
              <div className="ac-head">
                <div className="ac-bank">
                  <Building2 size={17} />
                  <strong>{a.bank}</strong>
                </div>
                <div className="ac-actions">
                  <button
                    className="icon-btn ghost"
                    onClick={() => { setEditing(a); setModal(true); }}
                    data-testid={`edit-account-${a.id}`}
                    aria-label="Düzenle"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    className="icon-btn ghost"
                    onClick={() => onDelete(a)}
                    data-testid={`delete-account-${a.id}`}
                    aria-label="Sil"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="ac-balance">{money(a.balance)}</div>
              <div className="ac-foot">
                <span>•••• {a.last_four}</span>
                <span>{a.currency || "TRY"}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <AccountModal open={modal} onClose={closeModal} onSave={onSave} initial={editing} />
    </>
  );
}
